import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import * as z from "zod";
import {
  IconMail,
  IconLoader2,
  IconSend,
  IconBuildingStore,
  IconUsersGroup,
} from "@tabler/icons-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useIdempotencyKey } from "@/hooks/useIdempotencyKey";
import { inviteBroker, InviteRejectedError } from "../api";
import type { BrokerLinkType } from "../api";
import { cn } from "@/lib/utils/cn";

type InviteForm = { email: string; linkType: BrokerLinkType };

/**
 * Os dois vínculos são exclusivos (a licença é paga pela imobiliária); o que muda é a
 * exclusividade da CAPTAÇÃO — e, com ela, o que a imobiliária enxerga fora dos imóveis
 * que ela mesma distribuiu (ADR-0039). Por isso a escolha é explícita, sem default.
 */
const LINK_TYPES: { value: BrokerLinkType; icon: typeof IconBuildingStore }[] = [
  { value: "internal", icon: IconBuildingStore },
  { value: "external", icon: IconUsersGroup },
];

interface InviteBrokerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteBrokerModal({ open, onOpenChange }: InviteBrokerModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const inviteSchema = useMemo(
    () =>
      z.object({
        email: z.string().email(t("brokers.modal.emailInvalid")),
        linkType: z.enum(["internal", "external"], {
          message: t("brokers.modal.linkTypeRequired"),
        }),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<InviteForm>({
    resolver: zodResolver(inviteSchema),
  });

  const selectedLinkType = watch("linkType");

  // A chave acompanha o formulário: se a rede falhar e a pessoa clicar de novo, é a MESMA
  // operação — e o backend devolve o convite já criado em vez de gastar outro seat.
  const { key: idempotencyKey, rotate: rotateIdempotencyKey } = useIdempotencyKey();

  const inviteMutation = useMutation({
    mutationFn: (data: InviteForm) => inviteBroker(data.email, data.linkType, idempotencyKey()),
    onSuccess: () => {
      // Convite concluído: o próximo é outra operação.
      rotateIdempotencyKey();
      toast.success(t("brokers.toast.invited"));
      reset();
      onOpenChange(false);
      queryClient.invalidateQueries({ queryKey: ["agency-brokers-list"] });
      queryClient.invalidateQueries({ queryKey: ["agency-seats"] });
    },
    onError: (error: Error) => {
      // NoSeatsAvailableError já vem com mensagem amigável do seat-store (#162).
      if (error?.name === "NoSeatsAvailableError") {
        toast.error(error.message);
        return;
      }
      // Recusa do crm (hub#686): o seat já voltou ao plano, falta dizer o porquê.
      if (error instanceof InviteRejectedError) {
        toast.error(
          t([`brokers.toast.rejected.${error.code}`, "brokers.toast.rejected.default"]),
        );
        return;
      }
      toast.error(t("brokers.toast.inviteError"));
    },
  });

  const onSubmit = (data: InviteForm) => {
    inviteMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("brokers.modal.title")}</DialogTitle>
          <DialogDescription>{t("brokers.modal.desc")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="invite-email" className="text-sm font-bold text-ink">
              {t("brokers.modal.emailLabel")}
            </label>
            <div className="relative">
              <IconMail
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft"
              />
              <input
                id="invite-email"
                type="email"
                placeholder={t("brokers.modal.emailPlaceholder")}
                className={cn(
                  "w-full rounded-xl border border-border bg-gray-50 pl-10 pr-4 py-2.5 text-sm outline-none transition-all focus:border-brand-500 focus:bg-white focus:ring-4 focus:ring-brand-500/10",
                  errors.email &&
                    "border-red-500 focus:border-red-500 focus:ring-red-500/10",
                )}
                {...register("email")}
              />
            </div>
            {errors.email && (
              <p className="text-xs font-bold text-red-500">{errors.email.message}</p>
            )}
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-bold text-ink">
              {t("brokers.modal.linkTypeLabel")}
            </legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {LINK_TYPES.map(({ value, icon: Icon }) => (
                <label
                  key={value}
                  className={cn(
                    "flex cursor-pointer flex-col gap-1 rounded-xl border p-3 transition-all",
                    "focus-within:ring-4 focus-within:ring-brand-500/10",
                    selectedLinkType === value
                      ? "border-brand-500 bg-brand-grad-soft"
                      : "border-border bg-gray-50 hover:border-brand-300",
                  )}
                >
                  <span className="flex items-center gap-2 text-sm font-bold text-ink">
                    <input
                      type="radio"
                      value={value}
                      className="sr-only"
                      {...register("linkType")}
                    />
                    <Icon size={18} className="text-brand-600" />
                    {t(`brokers.linkType.${value}`)}
                  </span>
                  <span className="text-xs text-ink-muted">
                    {t(`brokers.linkType.${value}Desc`)}
                  </span>
                </label>
              ))}
            </div>
            {errors.linkType && (
              <p className="text-xs font-bold text-red-500">
                {errors.linkType.message}
              </p>
            )}
          </fieldset>

          <DialogFooter className="pt-4">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-xl px-4 py-2 text-sm font-bold text-ink-muted hover:bg-gray-100 transition-colors"
            >
              {t("brokers.modal.cancel")}
            </button>
            <button
              type="submit"
              disabled={inviteMutation.isPending}
              className={cn(
                "inline-flex items-center gap-2 rounded-xl px-6 py-2 text-sm font-black uppercase tracking-wider text-white transition-all shadow-soft",
                "bg-brand-grad hover:scale-105 hover:shadow-glow active:scale-95 disabled:opacity-50",
              )}
            >
              {inviteMutation.isPending ? (
                <IconLoader2 size={18} className="animate-spin" />
              ) : (
                <IconSend size={18} />
              )}
              {t("brokers.modal.submit")}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
